'use client';

import { useEffect, useState, type ReactNode } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useSession, signOut } from 'next-auth/react';
import {
  ArrowRight,
  Baby,
  Beef,
  BookOpen,
  Briefcase,
  Building2,
  Cake,
  CalendarDays,
  ChevronDown,
  Clapperboard,
  Feather,
  Flame,
  Flower2,
  Gem,
  Ghost,
  GraduationCap,
  Handshake,
  Heart,
  HeartHandshake,
  House,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  Layers,
  Leaf,
  LogOut,
  Mail,
  Megaphone,
  Menu,
  Network,
  PartyPopper,
  PlaneTakeoff,
  Snowflake,
  Tag,
  TreePalm,
  User,
  Users,
  UtensilsCrossed,
  Wine,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/ui/sheet';

interface NavLink {
  href: string;
  label: string;
  icon: LucideIcon;
  description?: string;
}

// One label set, shared by the desktop nav, the mobile drawer and the footer.
// Category slugs must match the backend (GET /category/search/).
const OCCASIONS: NavLink[] = [
  { href: '/categories/birthday-cards', label: 'Birthday', icon: Cake },
  { href: '/categories/farewell-cards', label: 'Farewell', icon: PlaneTakeoff },
  { href: '/categories/thank-you-cards', label: 'Thank you', icon: HeartHandshake },
  { href: '/categories/retirement-cards', label: 'Retirement', icon: TreePalm },
  { href: '/categories/office-cards', label: 'Work & office', icon: Briefcase },
  { href: '/categories/get-well-soon-cards', label: 'Get well soon', icon: Flower2 },
  { href: '/categories/sympathy-cards', label: 'Sympathy', icon: Feather },
  { href: '/categories/wedding-cards', label: 'Wedding', icon: Gem },
  { href: '/categories/anniversary-cards', label: 'Anniversary', icon: Wine },
  { href: '/categories/baby-shower-cards', label: 'Baby shower', icon: Baby },
  { href: '/categories/congratulation-cards', label: 'Congratulations', icon: PartyPopper },
  { href: '/categories/graduation-cards', label: 'Graduation', icon: GraduationCap },
];

const CARD_EXTRAS: NavLink[] = [
  { href: '/categories', label: 'All card designs', icon: LayoutGrid, description: 'Every occasion in one place' },
  { href: '/bulk-cards', label: 'Bulk cards', icon: Layers, description: 'Set up many cards from a spreadsheet' },
];

const IDEAS: NavLink[] = [
  { href: '/inspiration-calendar', label: 'Inspiration calendar', icon: CalendarDays, description: 'Holidays and occasions by month' },
  { href: '/blog', label: 'Message ideas', icon: BookOpen, description: 'Wishes and quotes for every card' },
];

// Invitation categories with designs (slugs match the backend's invitation categories)
const INVITATIONS: NavLink[] = [
  { href: '/invitations/birthday', label: 'Birthday party', icon: Cake },
  { href: '/invitations/wedding', label: 'Wedding', icon: Gem },
  { href: '/invitations/baby-shower', label: 'Baby shower', icon: Baby },
  { href: '/invitations/anniversary', label: 'Anniversary', icon: Wine },
  { href: '/invitations/engagement-party', label: 'Engagement', icon: Heart },
  { href: '/invitations/bridal-shower', label: 'Bridal shower', icon: Flower2 },
  { href: '/invitations/graduation-party', label: 'Graduation', icon: GraduationCap },
  { href: '/invitations/housewarming', label: 'Housewarming', icon: House },
  { href: '/invitations/retirement-and-farewell', label: 'Retirement & farewell', icon: TreePalm },
  { href: '/invitations/dinner-party', label: 'Dinner party', icon: UtensilsCrossed },
  { href: '/invitations/holiday-party', label: 'Holiday party', icon: Snowflake },
  { href: '/invitations/diwali', label: 'Diwali', icon: Flame },
];

const INVITATION_EXTRAS: NavLink[] = [
  { href: '/invitations', label: 'All invitation designs', icon: LayoutGrid, description: 'Every event in one place' },
  { href: '/invitations/categories', label: 'Browse by category', icon: Layers, description: 'Find the right style fast' },
];

const INVITATION_SEASONAL: NavLink[] = [
  { href: '/invitations/halloween-party', label: 'Halloween', icon: Ghost },
  { href: '/invitations/new-years-eve-party', label: "New Year's Eve", icon: PartyPopper },
  { href: '/invitations/thanksgiving', label: 'Thanksgiving', icon: Leaf },
  { href: '/invitations/bbq', label: 'BBQ', icon: Beef },
];

const TEAMS: NavLink[] = [
  { href: '/solutions/hr', label: 'Human resources', icon: Users, description: 'Onboarding, birthdays and farewells' },
  { href: '/solutions/department', label: 'Departments', icon: Network, description: 'Cards for every team' },
  { href: '/solutions/business', label: 'Business (SMB)', icon: Building2, description: 'For small and growing companies' },
  { href: '/solutions/enterprise', label: 'Enterprise & industry', icon: Landmark, description: 'Recognition at scale' },
  { href: '/solutions/education-community', label: 'Education & community', icon: GraduationCap, description: 'Schools, clubs and nonprofits' },
  { href: '/solutions/client-customer', label: 'Clients & customers', icon: Handshake, description: 'Thank the people you serve' },
  { href: '/solutions/events', label: 'Events', icon: Megaphone, description: 'Conferences and company events' },
];

const ACCOUNT_LINKS: NavLink[] = [
  { href: '/account/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/account/cards', label: 'My cards', icon: LayoutGrid },
  { href: '/account/my-invitations', label: 'My invitations', icon: Mail },
  { href: '/account/profile', label: 'Profile', icon: User },
];

const FOOTNOTE_LINKS = [
  { href: '/blog', label: 'Blog' },
  { href: '/faq', label: 'Help centre' },
  { href: '/about', label: 'About' },
  { href: '/contact', label: 'Contact' },
];

const PRIMARY_CTA_LABEL = 'Start a group card';
const MOBILE_MENU_ID = 'mobile-navigation';
const LOGO_SRC = '/assets/images/logos/lovingecards-logo.svg';

// Which top-level item owns the current page.
const SECTION_PREFIXES = {
  cards: ['/categories', '/bulk-cards', '/card/', '/create', '/inspiration-calendar'],
  invitations: ['/invitations'],
  video: ['/group-video-cards'],
  teams: ['/solutions'],
  pricing: ['/pricing'],
} as const;

type Section = keyof typeof SECTION_PREFIXES;

const initialsOf = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]!.toUpperCase()).join('') || 'U';

const triggerClass = (active: boolean) =>
  `group relative inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-full px-2.5 text-sm font-medium transition-colors xl:px-4 xl:text-[0.9375rem] hover:bg-paper hover:text-ink data-[state=open]:bg-paper data-[state=open]:text-ink ${
    active ? 'text-ink after:absolute after:inset-x-4 after:-bottom-[13px] after:h-0.5 after:rounded-full after:bg-heart-500' : 'text-gray-700'
  }`;

const menuItemClass = 'cursor-pointer rounded-xl p-0 focus:bg-paper data-[highlighted]:bg-paper';

function PanelHeading({ children }: { children: ReactNode }) {
  return <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">{children}</p>;
}

/** Icon + label (+ description) row used in every dropdown panel. */
function PanelLink({ link }: { link: NavLink }) {
  const Icon = link.icon;
  return (
    <DropdownMenuItem asChild className={menuItemClass}>
      <Link href={link.href} className="flex items-start gap-3 px-3 py-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white ring-1 ring-paper-rule">
          <Icon className="h-[18px] w-[18px] text-heart-600" strokeWidth={1.75} aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-ink">{link.label}</span>
          {link.description && <span className="block text-sm text-gray-600">{link.description}</span>}
        </span>
      </Link>
    </DropdownMenuItem>
  );
}

function MobileSection({
  id,
  label,
  open,
  onToggle,
  children,
}: {
  id: string;
  label: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <li className="border-b border-paper-rule">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`mobile-${id}`}
        onClick={onToggle}
        className="flex w-full items-center justify-between py-4 text-left text-lg font-medium text-ink"
      >
        {label}
        <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      <div id={`mobile-${id}`} className={`grid transition-[grid-template-rows] duration-200 ease-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden" inert={!open}>
          <div className="pb-5">{children}</div>
        </div>
      </div>
    </li>
  );
}

function MobileRowLink({ link }: { link: NavLink }) {
  const Icon = link.icon;
  return (
    <Link href={link.href} className="flex items-center gap-3 rounded-xl px-2 py-2.5 text-base text-gray-700 transition-colors hover:bg-paper hover:text-ink">
      <Icon className="h-5 w-5 shrink-0 text-heart-600" strokeWidth={1.75} aria-hidden="true" />
      {link.label}
    </Link>
  );
}

export default function Header() {
  const pathname = usePathname() ?? '/';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const { data: session, status } = useSession();
  const user =
    status === 'authenticated' && session
      ? { name: session.user?.name || 'Your account', email: session.user?.email || '' }
      : null;

  const isActive = (section: Section) =>
    SECTION_PREFIXES[section].some((prefix) => pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`));

  // Close the drawer whenever the route changes.
  useEffect(() => {
    setIsMenuOpen(false);
    setOpenSection(null);
  }, [pathname]);

  // The bar only gets its rule and shadow once the page has scrolled.
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleSection = (id: string) => setOpenSection((current) => (current === id ? null : id));

  const logout = async () => {
    try {
      await signOut({ redirect: false });
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 h-[var(--nav-height)] border-b bg-white/95 backdrop-blur-xl transition-[border-color,box-shadow] duration-200 ${
          isScrolled ? 'border-paper-rule shadow-[0_1px_12px_rgba(6,35,63,0.06)]' : 'border-transparent'
        }`}
      >
        <div className="page-shell flex h-full items-center gap-4 lg:grid lg:grid-cols-[auto_1fr_auto] lg:gap-6 xl:grid-cols-[1fr_auto_1fr]">
          <Link href="/" className="shrink-0 justify-self-start rounded-md" aria-label="LovingEcards home">
            <Image src={LOGO_SRC} alt="LovingEcards" height={40} width={203} priority className="h-[30px] w-auto sm:h-9 lg:h-8 xl:h-10" />
          </Link>

          {/* Desktop navigation */}
          <nav aria-label="Main" className="hidden justify-self-center lg:block">
            <ul className="flex items-center gap-0.5 xl:gap-1">
              <li>
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger className={triggerClass(isActive('cards'))}>
                    Group Cards
                    <ChevronDown className="h-4 w-4 text-gray-500 transition-transform group-data-[state=open]:rotate-180" aria-hidden="true" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" sideOffset={14} className="w-[720px] overflow-hidden rounded-2xl border-paper-rule bg-white p-0 shadow-xl">
                    <div className="grid grid-cols-[1.35fr_1fr]">
                      <div className="p-4">
                        <PanelHeading>Cards by occasion</PanelHeading>
                        <div className="grid grid-cols-2 gap-0.5">
                          {OCCASIONS.map(({ href, label, icon: Icon }) => (
                            <DropdownMenuItem key={href} asChild className={menuItemClass}>
                              <Link href={href} className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700">
                                <Icon className="h-[18px] w-[18px] shrink-0 text-heart-600" strokeWidth={1.75} aria-hidden="true" />
                                {label}
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </div>
                      </div>
                      <div className="border-l border-paper-rule bg-paper/60 p-4">
                        <PanelHeading>Browse</PanelHeading>
                        {CARD_EXTRAS.map((link) => <PanelLink key={link.href} link={link} />)}
                        <DropdownMenuSeparator className="mx-3 my-2 bg-paper-rule" />
                        <PanelHeading>Ideas</PanelHeading>
                        {IDEAS.map((link) => <PanelLink key={link.href} link={link} />)}
                      </div>
                    </div>
                    <div className="flex items-center justify-between border-t border-paper-rule bg-white px-7 py-3.5">
                      <p className="text-sm text-gray-600">Free during launch · Signers don’t need an account</p>
                      <DropdownMenuItem asChild className="cursor-pointer rounded-full p-0 focus:bg-transparent">
                        <Link href="/categories" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink underline decoration-heart-300 underline-offset-4 hover:decoration-heart-600">
                          {PRIMARY_CTA_LABEL}
                          <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>

              <li>
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger className={triggerClass(isActive('invitations'))}>
                    Invitations
                    <ChevronDown className="h-4 w-4 text-gray-500 transition-transform group-data-[state=open]:rotate-180" aria-hidden="true" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" sideOffset={14} className="w-[720px] overflow-hidden rounded-2xl border-paper-rule bg-white p-0 shadow-xl">
                    <div className="grid grid-cols-[1.35fr_1fr]">
                      <div className="p-4">
                        <PanelHeading>Invitations by event</PanelHeading>
                        <div className="grid grid-cols-2 gap-0.5">
                          {INVITATIONS.map(({ href, label, icon: Icon }) => (
                            <DropdownMenuItem key={href} asChild className={menuItemClass}>
                              <Link href={href} className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700">
                                <Icon className="h-[18px] w-[18px] shrink-0 text-heart-600" strokeWidth={1.75} aria-hidden="true" />
                                {label}
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </div>
                      </div>
                      <div className="border-l border-paper-rule bg-paper/60 p-4">
                        <PanelHeading>Browse</PanelHeading>
                        {INVITATION_EXTRAS.map((link) => <PanelLink key={link.href} link={link} />)}
                        <DropdownMenuSeparator className="mx-3 my-2 bg-paper-rule" />
                        <PanelHeading>Seasonal</PanelHeading>
                        <div className="grid grid-cols-2 gap-0.5">
                          {INVITATION_SEASONAL.map(({ href, label, icon: Icon }) => (
                            <DropdownMenuItem key={href} asChild className={menuItemClass}>
                              <Link href={href} className="flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-gray-700">
                                <Icon className="h-4 w-4 shrink-0 text-heart-600" strokeWidth={1.75} aria-hidden="true" />
                                {label}
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between border-t border-paper-rule bg-white px-7 py-3.5">
                      <p className="text-sm text-gray-600">Free · Guests RSVP without an account</p>
                      <DropdownMenuItem asChild className="cursor-pointer rounded-full p-0 focus:bg-transparent">
                        <Link href="/invitations" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink underline decoration-heart-300 underline-offset-4 hover:decoration-heart-600">
                          Create an invitation
                          <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>

              <li>
                <Link href="/group-video-cards" aria-current={isActive('video') ? 'page' : undefined} className={triggerClass(isActive('video'))}>
                  Group Video
                </Link>
              </li>

              <li>
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger className={triggerClass(isActive('teams'))}>
                    For Teams
                    <ChevronDown className="h-4 w-4 text-gray-500 transition-transform group-data-[state=open]:rotate-180" aria-hidden="true" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" sideOffset={14} className="w-[600px] rounded-2xl border-paper-rule bg-white p-3 shadow-xl">
                    <PanelHeading>LovingEcards for teams</PanelHeading>
                    <div className="grid grid-cols-2 gap-0.5">
                      {TEAMS.map((link) => <PanelLink key={link.href} link={link} />)}
                      <PanelLink link={CARD_EXTRAS[1]} />
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>

              <li>
                <Link href="/pricing" aria-current={isActive('pricing') ? 'page' : undefined} className={triggerClass(isActive('pricing'))}>
                  Pricing
                </Link>
              </li>
            </ul>
          </nav>

          {/* Right side */}
          <div className="ml-auto flex items-center gap-1 justify-self-end sm:gap-2">
            <div className="hidden items-center gap-2 lg:flex">
              {status === 'loading' ? (
                <span className="h-10 w-40 animate-pulse rounded-full bg-paper" aria-hidden="true" />
              ) : user === null ? (
                <>
                  <Link href="/auth/login" className="inline-flex h-10 items-center rounded-full whitespace-nowrap px-3 text-[0.9375rem] font-medium text-gray-700 transition-colors hover:bg-paper hover:text-ink xl:px-4">
                    Sign in
                  </Link>
                  <Link
                    href="/auth/register"
                    className="inline-flex h-10 items-center whitespace-nowrap rounded-full border border-ink/20 px-3.5 text-[0.9375rem] xl:px-4 font-semibold text-ink transition-colors hover:border-ink hover:bg-paper"
                  >
                    Create account
                  </Link>
                </>
              ) : (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger
                    className="group flex items-center gap-2 rounded-full py-1 pl-1 pr-2.5 transition-colors hover:bg-paper data-[state=open]:bg-paper"
                    aria-label={`Account menu for ${user.name}`}
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-xs font-semibold text-white">
                      {initialsOf(user.name)}
                    </span>
                    <span className="hidden max-w-[8rem] truncate text-sm font-medium text-ink xl:block">{user.name.split(' ')[0]}</span>
                    <ChevronDown className="h-4 w-4 text-gray-500 transition-transform group-data-[state=open]:rotate-180" aria-hidden="true" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" sideOffset={12} className="w-64 rounded-2xl border-paper-rule bg-white p-2 shadow-xl">
                    <div className="px-3 pb-3 pt-2">
                      <p className="truncate text-sm font-semibold text-ink">{user.name}</p>
                      {user.email && <p className="truncate text-sm text-gray-600">{user.email}</p>}
                    </div>
                    <DropdownMenuSeparator className="mb-1 bg-paper-rule" />
                    {ACCOUNT_LINKS.map(({ href, label, icon: Icon }) => (
                      <DropdownMenuItem key={href} asChild className={menuItemClass}>
                        <Link href={href} className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700">
                          <Icon className="h-[18px] w-[18px] text-gray-500" strokeWidth={1.75} aria-hidden="true" />
                          {label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator className="my-1 bg-paper-rule" />
                    <DropdownMenuItem
                      className="cursor-pointer gap-3 rounded-xl px-3 py-2 text-sm font-medium text-gray-700 focus:bg-paper"
                      onSelect={() => { void logout(); }}
                    >
                      <LogOut className="h-[18px] w-[18px] text-gray-500" strokeWidth={1.75} aria-hidden="true" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Phones and tablets: quick way into the account, then the menu */}
            {status !== 'loading' && (
              user === null ? (
                <Link href="/auth/login" className="inline-flex h-10 items-center rounded-full px-3 text-[0.9375rem] font-medium text-gray-700 transition-colors hover:bg-paper hover:text-ink lg:hidden">
                  Sign in
                </Link>
              ) : (
                <Link
                  href="/account/dashboard"
                  aria-label={`Your account, ${user.name}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-paper lg:hidden"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-xs font-semibold text-white">{initialsOf(user.name)}</span>
                </Link>
              )
            )}

            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
              aria-controls={MOBILE_MENU_ID}
              onClick={() => setIsMenuOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors hover:bg-paper lg:hidden"
            >
              <Menu className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <SheetContent
          id={MOBILE_MENU_ID}
          side="right"
          aria-describedby={undefined}
          className="flex w-full flex-col gap-0 border-l-0 bg-white p-0 sm:max-w-md lg:hidden [&>button:first-child]:hidden"
          // Also close for links to the current page, which don't change the route.
          onClick={(e) => { if ((e.target as HTMLElement).closest('a')) setIsMenuOpen(false); }}
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>

          <div className="flex h-[var(--nav-height)] shrink-0 items-center justify-between border-b border-paper-rule px-4 sm:px-6">
            <Link href="/" aria-label="LovingEcards home">
              <Image src={LOGO_SRC} alt="LovingEcards" height={34} width={172} className="h-[30px] w-auto" />
            </Link>
            <SheetClose className="flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors hover:bg-paper" aria-label="Close menu">
              <X className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
            </SheetClose>
          </div>

          <nav aria-label="Mobile" className="flex-1 overflow-y-auto px-4 pb-6 sm:px-6">
            {user && (
              <div className="mt-4 rounded-2xl bg-paper p-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink text-sm font-semibold text-white">
                    {initialsOf(user.name)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-semibold text-ink">{user.name}</span>
                    {user.email && <span className="block truncate text-sm text-gray-600">{user.email}</span>}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {ACCOUNT_LINKS.map(({ href, label, icon: Icon }) => (
                    <Link key={href} href={href} className="flex items-center gap-2 rounded-xl bg-white px-3 py-2.5 text-sm font-medium text-gray-700 ring-1 ring-paper-rule hover:text-ink">
                      <Icon className="h-4 w-4 text-gray-500" strokeWidth={1.75} aria-hidden="true" />
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <ul className="mt-2">
              <MobileSection id="cards" label="Group Cards" open={openSection === 'cards'} onToggle={() => toggleSection('cards')}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">By occasion</p>
                <div className="grid grid-cols-2 gap-2">
                  {OCCASIONS.map(({ href, label, icon: Icon }) => (
                    <Link key={href} href={href} className="flex items-center gap-2 rounded-xl border border-paper-rule px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:border-ink-300 hover:text-ink">
                      <Icon className="h-4 w-4 shrink-0 text-heart-600" strokeWidth={1.75} aria-hidden="true" />
                      <span className="truncate">{label}</span>
                    </Link>
                  ))}
                </div>
                <div className="mt-3">
                  {[...CARD_EXTRAS, ...IDEAS].map((link) => <MobileRowLink key={link.href} link={link} />)}
                </div>
              </MobileSection>

              <MobileSection id="invitations" label="Invitations" open={openSection === 'invitations'} onToggle={() => toggleSection('invitations')}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">By event</p>
                <div className="grid grid-cols-2 gap-2">
                  {[...INVITATIONS, ...INVITATION_SEASONAL].map(({ href, label, icon: Icon }) => (
                    <Link key={href} href={href} className="flex items-center gap-2 rounded-xl border border-paper-rule px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:border-ink-300 hover:text-ink">
                      <Icon className="h-4 w-4 shrink-0 text-heart-600" strokeWidth={1.75} aria-hidden="true" />
                      <span className="truncate">{label}</span>
                    </Link>
                  ))}
                </div>
                <div className="mt-3">
                  {INVITATION_EXTRAS.map((link) => <MobileRowLink key={link.href} link={link} />)}
                </div>
              </MobileSection>

              <li className="border-b border-paper-rule">
                <Link href="/group-video-cards" className="flex items-center justify-between py-4 text-lg font-medium text-ink">
                  Group Video
                  <Clapperboard className="h-5 w-5 text-gray-500" strokeWidth={1.75} aria-hidden="true" />
                </Link>
              </li>

              <MobileSection id="teams" label="For Teams" open={openSection === 'teams'} onToggle={() => toggleSection('teams')}>
                {[...TEAMS, CARD_EXTRAS[1]].map((link) => <MobileRowLink key={link.href} link={link} />)}
              </MobileSection>

              <li className="border-b border-paper-rule">
                <Link href="/pricing" className="flex items-center justify-between py-4 text-lg font-medium text-ink">
                  Pricing
                  <Tag className="h-5 w-5 text-gray-500" strokeWidth={1.75} aria-hidden="true" />
                </Link>
              </li>
            </ul>

            <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
              {FOOTNOTE_LINKS.map(({ href, label }) => (
                <li key={href}>
                  <Link href={href} className="text-sm font-medium text-gray-600 hover:text-ink">{label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="shrink-0 border-t border-paper-rule bg-white px-4 py-4 sm:px-6">
            {user === null ? (
              <div className="grid grid-cols-2 gap-3">
                <Button asChild variant="soft" shape="pill" className="h-12 text-base">
                  <Link href="/auth/login">Sign in</Link>
                </Button>
                <Button asChild shape="pill" className="h-12 text-base">
                  <Link href="/auth/register">Create account</Link>
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { void logout(); }}
                className="flex w-full items-center justify-center gap-2 py-1 text-sm font-medium text-gray-600 hover:text-ink"
              >
                <LogOut className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                Sign out
              </button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
